import { useEffect, useLayoutEffect, useRef, useState, type KeyboardEvent, type ChangeEvent } from "react";
import { Button } from "../components/Button";
import { Field } from "../components/FormControls";
import { navigate, useLocationUrl } from "../routing/useLocationPath";
import { currentReportingYear, reportingPeriodError, validReportingDate, type ReportingPeriod } from "./reportingFilterState";
export { currentReportingYear, periodQuery, type ReportingPeriod } from "./reportingFilterState";

export function useReportSearch() { return new URL(useLocationUrl(), window.location.origin).search; }
export function setReportSearch(query: string, replace = false) {
  navigate(`${window.location.pathname}${query ? `?${query}` : ""}${window.location.hash}`, {replace});
}
export function useReportingPeriod() {
  const params = new URLSearchParams(useReportSearch()), defaults = currentReportingYear();
  return {from: params.get("from") ?? defaults.from, to: params.get("to") ?? defaults.to};
}

// Picker selections apply on change. Segmented numeric typing is a draft until
// the native edit is committed with Enter/blur, so a half-typed year is never sent.
export function useAutomaticReportFields<T extends ReportingPeriod>(applied: T, apply: (fields: T) => void, validate: (fields: T) => string = reportingPeriodError) {
  const [fields, setFields] = useState(applied), current = useRef(fields), editing = useRef(false);
  const appliedKey = JSON.stringify(applied);
  useEffect(() => { current.current = applied; setFields(applied); editing.current = false; }, [appliedKey]);
  function update(patch: Partial<T>, defer = false) {
    const next = {...current.current, ...patch}; current.current = next; setFields(next);
    if (!defer && !validate(next)) apply(next);
  }
  function commit() { editing.current = false; if (!validate(current.current)) apply(current.current); }
  function dateInput(name: "from" | "to") {
    return {type: "date" as const, required: true, value: fields[name],
      min: name === "to" && validReportingDate(fields.from) ? fields.from : undefined,
      max: name === "from" && validReportingDate(fields.to) ? fields.to : undefined,
      "aria-invalid": Boolean(reportingPeriodError(fields)) || undefined,
      onChange: (event: ChangeEvent<HTMLInputElement>) => update({[name]: event.currentTarget.value} as Partial<T>, editing.current),
      onKeyDown: (event: KeyboardEvent<HTMLInputElement>) => {
        if (/^\d$/.test(event.key) || ["Backspace", "Delete"].includes(event.key)) editing.current = true;
        if (event.key === "Enter") { event.preventDefault(); commit(); }
      }, onBlur: commit};
  }
  return {fields, update, commit, dateInput, error: validate(fields)};
}
export function ReportingPeriodControls({period, onValidityChange}: {period: ReportingPeriod; onValidityChange?(valid: boolean): void}) {
  const draft = useAutomaticReportFields(period, next => {
    const params = new URLSearchParams(window.location.search); params.set("from", next.from); params.set("to", next.to); setReportSearch(params.toString());
  });
  useLayoutEffect(() => onValidityChange?.(!draft.error && draft.fields.from === period.from && draft.fields.to === period.to), [draft.error, draft.fields.from, draft.fields.to, period.from, period.to, onValidityChange]);
  return <>
    <form className="report-period-controls" aria-label="Reporting period" onSubmit={event => {event.preventDefault(); draft.commit();}}>
      <Field label="From" {...draft.dateInput("from")}/><Field label="To" {...draft.dateInput("to")}/>
      <Button variant="quiet" onClick={() => draft.update(currentReportingYear())}>This year</Button>
    </form>
    {draft.error ? <p className="sbx-cell-secondary" role="status">{draft.error}</p> : null}
  </>;
}
export function ReportRefreshIndicator({loading, hasResult, error = ""}: {loading: boolean; hasResult: boolean; error?: string}) {
  const text = loading && hasResult ? "Updating report. Previous completed results remain displayed; exports will be available when the update completes." : error && hasResult ? "Update failed. Previous completed results remain displayed. Retry to apply the selected filters." : hasResult ? "Report updated." : "";
  return <span className="report-refresh-indicator" data-refreshing={loading && hasResult} role="status" aria-live="polite"><span aria-hidden="true">↻</span><span className="visually-hidden">{text}</span></span>;
}
